import { ISSUE_STATE_LABEL, SEVERITIES, type IssueState, type IssueSummary } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Columns3, Hourglass, Inbox, List, RotateCcw, X } from "lucide-react";
import { useMemo } from "react";
import { GraceCountdown, useNow } from "@/components/domain/countdown";
import { AgentLink, PageHeader, TimeAgo } from "@/components/domain/misc";
import { CoverageChip, IssueStateBadge, SeverityPill } from "@/components/domain/status";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Select } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IssueBoard } from "@/features/issues/board";
import { BOARD_COLUMNS, type BoardColumnKey, boardColumnOf, byOpenSeverityDeadline, hasActiveGrace, isInFinalStretch, type IssuesSearch, parseIssuesSearch, SEVERITY_RANK } from "@/features/issues/lib";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/issues/")({
  validateSearch: parseIssuesSearch,
  component: IssuesPage,
});

/** `grace` is a board column that also holds regressed issues. */
function matchesState(i: IssueSummary, state: IssueState | undefined) {
  if (!state) return true;
  return state === "grace" ? boardColumnOf(i.state) === "grace" : i.state === state;
}

function IssuesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const view = search.view ?? "list";
  const setSearch = (patch: Partial<IssuesSearch>) =>
    void navigate({ search: (prev: IssuesSearch) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined && v !== "")) as IssuesSearch, replace: true });

  // Fetch by agent/severity; state is filtered client-side so the summary strip can count every state.
  const issuesQ = useQuery(q.issues({ agentId: search.agentId, severity: search.severity ? [search.severity] : undefined }));
  const agentsQ = useQuery(q.agents());

  const all = issuesQ.data?.items;
  const filtered = useMemo(() => (all ?? []).filter((i) => matchesState(i, search.state)).sort(byOpenSeverityDeadline), [all, search.state]);
  const hasFilters = Boolean(search.severity || search.state || search.agentId);

  return (
    <Page>
      <PageHeader
        title="Issues"
        description="Detected behavioral risks across your agents. Confirmed issues stay covered through their grace window; a control enforced by the agent platform mitigates, a verified fix resolves."
        actions={
          <Tabs value={view} onValueChange={(v) => setSearch({ view: v === "board" ? "board" : undefined })}>
            <TabsList aria-label="View">
              <TabsTrigger value="list">
                <List className="size-3.5" /> List
              </TabsTrigger>
              <TabsTrigger value="board">
                <Columns3 className="size-3.5" /> Board
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      <SummaryStrip issues={all} activeState={search.state} onSelect={(s) => setSearch({ state: s === search.state ? undefined : s })} />

      <div className="mb-3 mt-4 flex flex-wrap items-center gap-2">
        <Select aria-label="Severity" value={search.severity ?? ""} onChange={(e) => setSearch({ severity: (e.target.value || undefined) as IssuesSearch["severity"] })} className="w-36">
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s[0]!.toUpperCase() + s.slice(1)}
            </option>
          ))}
        </Select>
        <Select aria-label="State" value={search.state ?? ""} onChange={(e) => setSearch({ state: (e.target.value || undefined) as IssuesSearch["state"] })} className="w-40">
          <option value="">All states</option>
          {(["detected", "grace", "regressed", "mitigated", "verifying", "resolved", "excluded"] as const).map((s) => (
            <option key={s} value={s}>
              {s === "grace" ? "Grace (incl. regressed)" : ISSUE_STATE_LABEL[s]}
            </option>
          ))}
        </Select>
        <Select aria-label="Agent" value={search.agentId ?? ""} onChange={(e) => setSearch({ agentId: e.target.value || undefined })} className="w-48">
          <option value="">All agents</option>
          {agentsQ.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        {hasFilters ? (
          <Button variant="ghost" size="sm" onClick={() => setSearch({ severity: undefined, state: undefined, agentId: undefined })}>
            <X /> Clear filters
          </Button>
        ) : null}
        <span className="ml-auto text-xs text-fg-3 tabular" aria-live="polite">
          {issuesQ.data ? `${fmtInt(filtered.length)} ${filtered.length === 1 ? "issue" : "issues"}` : null}
        </span>
      </div>

      {issuesQ.error ? (
        <ErrorPanel error={issuesQ.error} reset={() => void issuesQ.refetch()} />
      ) : view === "board" ? (
        <IssueBoard issues={issuesQ.data ? filtered : undefined} loading={issuesQ.isPending} />
      ) : (
        <IssueTable data={issuesQ.data ? filtered : undefined} loading={issuesQ.isPending} filtered={hasFilters} />
      )}
    </Page>
  );
}

// ─── Summary strip ───────────────────────────────────────────────────────────

function SummaryStrip({ issues, activeState, onSelect }: { issues: IssueSummary[] | undefined; activeState: IssueState | undefined; onSelect: (s: BoardColumnKey) => void }) {
  const now = useNow();
  const counts = new Map<BoardColumnKey, number>(BOARD_COLUMNS.map((c) => [c.key, 0]));
  let regressed = 0;
  for (const i of issues ?? []) {
    counts.set(boardColumnOf(i.state), (counts.get(boardColumnOf(i.state)) ?? 0) + 1);
    if (i.state === "regressed") regressed++;
  }
  const finalStretch = (issues ?? []).filter((i) => isInFinalStretch(i, now)).length;

  return (
    <Card className="flex flex-wrap items-stretch overflow-hidden">
      {BOARD_COLUMNS.map((c, idx) => {
        const active = activeState === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onSelect(c.key)}
            aria-pressed={active}
            title={c.hint}
            className={cn(
              "flex min-w-[120px] flex-1 cursor-pointer flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none",
              idx > 0 && "border-l",
              active && "bg-surface-2 shadow-[inset_0_-2px_0_var(--accent)]",
            )}
          >
            <span className="text-xs font-medium text-fg-3">{c.label}</span>
            <span className="flex items-baseline gap-1.5">
              <span className="text-xl font-semibold leading-none tabular">{issues ? counts.get(c.key) : "–"}</span>
              {c.key === "grace" && regressed > 0 ? (
                <span className="inline-flex items-center gap-0.5 text-[11px] text-fg-3">
                  <RotateCcw className="size-3" aria-hidden /> {regressed} regressed
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
      <div className={cn("flex min-w-[200px] flex-[1.4] items-center gap-3 border-l px-4 py-3", finalStretch > 0 ? "bg-critical/5" : undefined)}>
        <Hourglass className={cn("size-5 shrink-0", finalStretch > 0 ? "text-critical-text" : "text-fg-3")} aria-hidden />
        <div>
          <div className="text-xl font-semibold leading-none tabular">{issues ? finalStretch : "–"}</div>
          <div className="mt-1 text-xs text-fg-3">in final 20% of grace</div>
        </div>
      </div>
    </Card>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

const columns: ColumnDef<IssueSummary>[] = [
  {
    id: "id",
    header: "ID",
    accessorKey: "id",
    meta: { width: 84 },
    cell: ({ row }) => <span className="whitespace-nowrap font-mono text-[12px] text-fg-2">{row.original.id}</span>,
  },
  {
    id: "title",
    header: "Issue · agent",
    accessorKey: "title",
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex min-w-[260px] max-w-[400px] flex-col gap-1">
        <span className="line-clamp-2 font-medium leading-snug text-fg">{row.original.title}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-fg-3">
          <AgentLink id={row.original.agentId} name={row.original.agentName} className="shrink-0 whitespace-nowrap text-[11.5px]" />
          <span aria-hidden>·</span>
          <span className="truncate font-mono">{row.original.scenarioKey}</span>
        </span>
      </div>
    ),
  },
  {
    id: "severity",
    header: "Severity",
    accessorFn: (i) => SEVERITY_RANK[i.severity],
    sortDescFirst: false,
    cell: ({ row }) => <SeverityPill severity={row.original.severity} />,
  },
  {
    id: "state",
    header: "State",
    accessorKey: "state",
    cell: ({ row }) => <IssueStateBadge state={row.original.state} />,
  },
  {
    id: "coverage",
    header: "Coverage",
    accessorKey: "coverageState",
    cell: ({ row }) => <CoverageChip state={row.original.coverageState} />,
  },
  {
    id: "impact",
    header: "Sess. / users",
    accessorKey: "affectedSessions",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {fmtInt(row.original.affectedSessions)} <span className="text-fg-3">/ {fmtInt(row.original.affectedEndUsers)}</span>
      </span>
    ),
  },
  {
    id: "exposure",
    header: "Exposure",
    accessorFn: (i) => i.exposure.amount,
    meta: { align: "right" },
    cell: ({ row }) => fmtMoney(row.original.exposure, { compact: true }),
  },
  {
    id: "grace",
    header: "Grace",
    accessorFn: (i) => (hasActiveGrace(i) ? Date.parse(i.graceDeadline!) : Number.MAX_SAFE_INTEGER),
    sortDescFirst: false,
    cell: ({ row }) =>
      hasActiveGrace(row.original) ? <GraceCountdown start={row.original.graceStartedAt} deadline={row.original.graceDeadline} className="whitespace-nowrap" /> : <span className="text-xs text-fg-3">—</span>,
  },
  {
    id: "lastSeen",
    header: "Last seen",
    accessorKey: "lastSeen",
    cell: ({ row }) => <TimeAgo iso={row.original.lastSeen} className="text-xs" />,
  },
];

function IssueTable({ data, loading, filtered }: { data: IssueSummary[] | undefined; loading: boolean; filtered: boolean }) {
  const navigate = useNavigate();
  return (
    <Card className="p-0">
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        getRowId={(i) => i.id}
        onRowClick={(i) => void navigate({ to: "/issues/$issueId", params: { issueId: i.id } })}
        empty={
          filtered
            ? { title: "No issues match these filters", description: "Try clearing a filter or switching agents.", icon: <Inbox /> }
            : { title: "No issues detected", description: "When detectors flag a behavioral risk it shows up here with a suggested fix.", icon: <Inbox /> }
        }
      />
    </Card>
  );
}
